"use client";

import { Toolbar } from "@/components/ui/toolbar";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { type CentralIconName } from "@/types/central-icon";
import useIsMobile from "@/hooks/use-is-mobile";
import { usePipelineViewStore } from "@/features/jobs/store/pipeline/view";
import { usePipelineSearchQuery } from "@/features/jobs/hooks/use-pipeline-search-query";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import type { PipelineCandidate } from "@/lib/api/types";

interface PipelineToolbarProps {
  totalCount: number;
  onOpenViewOptions: () => void;
  onOpenExport: () => void;
  /** An "Export Profiles" job is starting or running. */
  exporting?: boolean;
  candidates?: PipelineCandidate[];
  onSelectAllCandidates?: () => void;
  /** Optional ATS action (the "Review" needs-merge button) shown for linked jobs. */
  atsAction?: React.ReactNode;
  /**
   * Mobile-only filter trigger. Desktop has the always-present filter rail, so
   * the node it renders returns `null` there and this slot collapses.
   */
  filterAction?: React.ReactNode;
}

export function PipelineToolbar({
  totalCount,
  onOpenViewOptions,
  onOpenExport,
  exporting = false,
  candidates = [],
  onSelectAllCandidates,
  atsAction,
  filterAction,
}: PipelineToolbarProps) {
  const { draft, setDraft } = usePipelineSearchQuery();
  // 768 to match this toolbar's own `md:` gates and the pipeline's `md:`
  // table/cards swap. The hook's 1024 default hid View Options across
  // 768–1023px, where the desktop column table is what's actually rendered.
  const isMobile = useIsMobile(768);

  return (
    <div
      data-tour="pipe-toolbar"
      className="border-b border-border flex items-center justify-between gap-3 h-[52px] px-4 py-3 shrink-0"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="md:hidden shrink-0">
          <MobileBulkSelect
            candidates={candidates}
            totalCount={totalCount}
            onSelectAllCandidates={onSelectAllCandidates}
          />
        </div>
        {filterAction}
        <div className="min-w-0 flex-1">
          <Toolbar.Search
            value={draft}
            onChange={setDraft}
            className="w-full md:w-[225px]"
          />
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2 md:gap-3">
        {atsAction}
        {!isMobile && (
          <ToolbarButton
            icon="IconSettingsGear2"
            label="View Options"
            onClick={onOpenViewOptions}
          />
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="bg-contrast/3 border-contrast/5 hover:bg-contrast/6"
          onClick={onOpenExport}
          disabled={totalCount === 0 || exporting}
          // Phones show the icon only, so the text can't name the button.
          aria-label="Export Profiles"
          icon={
            <div className="opacity-70">
              <Icon
                name={exporting ? "IconLoadingCircle" : "IconFileDownload"}
                size={16}
                className={cn("text-heading", exporting && "animate-spin")}
              />
            </div>
          }
        >
          <span className="hidden sm:inline">Export Profiles</span>
        </Button>
      </div>
    </div>
  );
}

function MobileBulkSelect({
  candidates,
  totalCount,
  onSelectAllCandidates,
}: {
  candidates: PipelineCandidate[];
  totalCount: number;
  onSelectAllCandidates?: () => void;
}) {
  const {
    bulkMode,
    selectedCandidateIds,
    excludedCandidateIds,
    selectionTotal,
    mergeSelected,
    selectAllCandidates,
    clearSelection,
  } = usePipelineViewStore();

  const isAllMode = bulkMode === "all";
  const pageCount = candidates.length;
  const allSelected =
    pageCount > 0 &&
    candidates.every((c) =>
      isAllMode
        ? !excludedCandidateIds.has(c.job_candidate_id)
        : selectedCandidateIds.has(c.job_candidate_id),
    );
  const someSelected = isAllMode
    ? Math.max(selectionTotal - excludedCandidateIds.size, 0) > 0
    : selectedCandidateIds.size > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="cursor-pointer">
          <div className="pointer-events-none">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={() => {}}
            />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="min-w-[200px]"
      >
        <DropdownMenuItem
          onClick={() =>
            mergeSelected(candidates.map((c) => c.job_candidate_id))
          }
        >
          <span className="text-foreground">Select this page</span>
          <span className="ml-0.5 text-muted-foreground">({pageCount})</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={
            onSelectAllCandidates ?? (() => selectAllCandidates(totalCount))
          }
        >
          <span className="text-foreground">Select all candidates</span>
          <span className="ml-0.5 text-muted-foreground">({totalCount})</span>
        </DropdownMenuItem>
        {someSelected && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={clearSelection}>
              Clear selection
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ToolbarButton({
  icon,
  label,
  hasChevron,
  hasToggle,
  onClick,
}: {
  icon: CentralIconName;
  label: string;
  hasChevron?: boolean;
  hasToggle?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1 bg-contrast/3 border border-contrast/5 rounded-md h-7 px-1.5 cursor-pointer hover:bg-contrast/6 transition-colors"
    >
      <div className="opacity-60">
        <Icon name={icon} size={16} className="text-heading" />
      </div>
      <span className="text-sm font-medium text-heading px-1 hidden sm:inline">
        {label}
      </span>
      {hasChevron && (
        <div className="opacity-40">
          <Icon
            name="IconChevronDownSmall"
            size={14}
            className="text-heading"
          />
        </div>
      )}
      {hasToggle && (
        <div className="opacity-40">
          <Icon
            name="IconChevronGrabberVertical"
            size={16}
            className="text-heading"
          />
        </div>
      )}
    </button>
  );
}
